import React, { useCallback, useRef } from "react"

import { Button, Col, Row } from "react-bootstrap"
import { FaMinus, FaPlus } from "react-icons/fa"
import { toast } from "react-toastify"

import useAuthFormProcessText from "../../../api/hooks/useAuthFormProcessText"
import OrderResultRecord from "../../../api/OrderResults/OrderResultModel/OrderResultRecord"
import { PhoneRegexp } from "../../../config/Constants"
import ActionButton from "../../ActionButton"
import PhoneNumberMaskedInput from "../../FormattedInputs/PhoneNumberMaskedInput"

export interface MobileItem {
  id: number
  value: string
}

interface MobileFormListProps {
  mobileItems: MobileItem[]
  setMobileItems: React.Dispatch<React.SetStateAction<MobileItem[]>>
  details?: OrderResultRecord
}

function MobileFormList({ mobileItems, setMobileItems, details = undefined }: MobileFormListProps) {
  const idCounter = useRef(0) // tracks unique IDs for mobile items. UUID would be better.

  const getNextId = useCallback(() => {
    idCounter.current += 1
    return idCounter.current
  }, [])

  const { processing, sendAuthFormByPhone } = useAuthFormProcessText()

  const isPhoneValid = useCallback((value: string) => PhoneRegexp.test(value), [])

  const canAddMobile = useCallback((value: string) => value.length > 0 && isPhoneValid(value), [isPhoneValid])

  const addMobile = useCallback(() => {
    setMobileItems((prev) => [...prev, { id: getNextId(), value: "" }])
  }, [getNextId, setMobileItems])

  const removeMobile = useCallback(
    (id: number) => {
      setMobileItems((prev) => prev.filter((item) => item.id !== id))
    },
    [setMobileItems]
  )

  const changeMobile = useCallback(
    (phone: string, id: number) => {
      setMobileItems((prev) => prev.map((item) => (item.id === id ? { ...item, value: phone } : item)))
    },
    [setMobileItems]
  )

  const handleSendText = useCallback(() => {
    const filtered = mobileItems.filter(Boolean)
    if (filtered.length === 0) {
      toast.error("Please enter mobile number.")
      return
    }
    if (new Set(mobileItems.map((v) => v.value.toLowerCase())).size !== mobileItems.length) {
      toast.error("Duplicate mobile number is present in the list.")
      return
    }
    sendAuthFormByPhone({
      emailAddress: mobileItems.map((v) => v.value),
      formFoxTestAN: Number(details?.FormFoxTestAN || 0),
      personId: details?.PersonID || 0,
      vendorId: details?.CollectionVendorID || 0,
    })
  }, [details, mobileItems, sendAuthFormByPhone])

  return (
    <>
      {mobileItems.map((item, index) => (
        <Row className="pt-2" key={`mobile-${item.id}`}>
          <Col xs={12} sm={3} md={3} lg={2}>
            {index === 0 && "Text form:"}
          </Col>
          <Col xs={9} sm={5} md={4}>
            <PhoneNumberMaskedInput
              size="sm"
              value={item.value}
              onChange={(value: string) => changeMobile(value, item.id)}
              readOnly={mobileItems.length !== index + 1}
              isInvalid={!!item.value.length && !isPhoneValid(item.value)}
            />
          </Col>
          <Col xs={3} sm={2} md={2}>
            {mobileItems.length === index + 1 ? (
              <Button
                size="sm"
                variant="primary"
                className="btn-sm-icon"
                onClick={addMobile}
                disabled={!canAddMobile(item.value)}
              >
                <FaPlus />
              </Button>
            ) : (
              <Button size="sm" variant="primary" className="btn-sm-icon" onClick={() => removeMobile(item.id)}>
                <FaMinus />
              </Button>
            )}
          </Col>
          {index === 0 && (
            <Col xs={3} sm={2} md={2}>
              <ActionButton
                size="sm"
                className="fixed-w10"
                onClick={handleSendText}
                disabled={mobileItems.some((email) => !isPhoneValid(email.value))}
                spin={processing}
              >
                Send Text
              </ActionButton>
            </Col>
          )}
        </Row>
      ))}
    </>
  )
}

export default MobileFormList
